import * as zmq from 'zeromq';
import { z85Decode, z85Encode } from './z85.js';

/**
 * ZAP (ZeroMQ RFC 27) authority: decides which CURVE client keys may connect
 * to each publisher in this process.
 *
 * libzmq consults ONE handler per context, bound at `inproc://zeromq.zap.01`,
 * so every publisher in the process shares it and identifies itself by its own
 * `zapDomain`. Each domain carries its own allowlist — a server's peer set.
 *
 * Publishers set `zapEnforceDomain`, so a handshake that finds no handler bound
 * is REFUSED rather than waved through: the failure mode of a missing handler
 * is "nobody can connect", never "anybody can".
 *
 * The handler socket is reference-counted by registered domains and closed
 * when the last one goes — an open zmq receive keeps the node process alive,
 * and a process whose servers have all closed must be able to exit.
 */
export type ZapDenial = { domain: string; publicKey: string; at: number };

const ZAP_ENDPOINT = 'inproc://zeromq.zap.01';
const MAX_DENIALS_KEPT = 200;

/**
 * Bind the handler endpoint. A previous handler's release can still be in
 * flight on libzmq's io thread (EADDRINUSE for a moment); wait for THAT
 * condition — the name becoming free — bounded, then fail loudly.
 */
async function bindReleasedEndpoint(socket: zmq.Reply): Promise<void> {
  const deadline = Date.now() + 5000;
  for (;;) {
    try {
      await socket.bind(ZAP_ENDPOINT);
      return;
    } catch (err: any) {
      if (err?.code !== 'EADDRINUSE' || Date.now() > deadline) {
        socket.close();
        throw new Error(`ZAP handler could not bind ${ZAP_ENDPOINT}: ${err?.message ?? String(err)} — with no handler every CURVE handshake is refused.`);
      }
      await new Promise(r => setImmediate(r));
    }
  }
}

export class ZapAuthority {
  private static readonly byContext = new WeakMap<object, ZapAuthority>();
  private static domainSeq = 0;

  /** The authority for zeromq's default context (the one every PubSub uses). */
  static get shared(): ZapAuthority {
    const ctx = zmq.context as unknown as object;
    let authority = ZapAuthority.byContext.get(ctx);
    if (!authority) {
      authority = new ZapAuthority();
      ZapAuthority.byContext.set(ctx, authority);
    }
    return authority;
  }

  private readonly domains = new Map<string, Set<string>>();
  private socket?: zmq.Reply;
  private chain: Promise<void> = Promise.resolve();
  /** Recent refusals, newest last — what an operator (or a test) reads to see an intruder. */
  readonly denials: ZapDenial[] = [];
  private readonly denialListeners = new Set<(d: ZapDenial) => void>();

  /** A fresh domain name, unique in this process. */
  static nextDomain(label: string | number): string {
    return `gotti/${label}/${++ZapAuthority.domainSeq}`;
  }

  /** Register a domain (empty allowlist) and make sure the handler is bound. */
  async open(domain: string): Promise<void> {
    if (!this.domains.has(domain)) this.domains.set(domain, new Set());
    await this.converge();
  }

  /** Drop a domain; the handler closes with the last one. */
  async close(domain: string): Promise<void> {
    this.domains.delete(domain);
    await this.converge();
  }

  allow(domain: string, publicKey: string): void {
    const set = this.domains.get(domain);
    if (!set) throw new Error(`ZAP domain ${domain} is not open.`);
    set.add(z85Decode(publicKey).toString('hex'));
  }

  revoke(domain: string, publicKey: string): void {
    this.domains.get(domain)?.delete(z85Decode(publicKey).toString('hex'));
  }

  isAllowed(domain: string, publicKey: string): boolean {
    return !!this.domains.get(domain)?.has(z85Decode(publicKey).toString('hex'));
  }

  onDenial(listener: (d: ZapDenial) => void): () => void {
    this.denialListeners.add(listener);
    return () => this.denialListeners.delete(listener);
  }

  private converge(): Promise<void> {
    const run = this.chain.then(async () => {
      if (this.domains.size > 0 && !this.socket) {
        const socket = new zmq.Reply({ linger: 0 });
        await bindReleasedEndpoint(socket);
        this.socket = socket;
        void this.serve(socket);
      } else if (this.domains.size === 0 && this.socket) {
        const socket = this.socket;
        this.socket = undefined;
        // Unbind BEFORE close: close() releases the inproc name asynchronously
        // on libzmq's io thread, and the next open() would race it.
        try { await socket.unbind(ZAP_ENDPOINT); } catch { /* already released */ }
        socket.close();
      }
    });
    this.chain = run.catch(() => undefined);
    return run;
  }

  private async serve(socket: zmq.Reply): Promise<void> {
    try {
      for await (const frames of socket) {
        const [version, requestId, domainFrame, , , mechanism, credential] = frames;
        const domain = domainFrame?.toString() ?? '';
        const allowed = mechanism?.toString() === 'CURVE'
          && credential?.length === 32
          && !!this.domains.get(domain)?.has(credential.toString('hex'));
        if (!allowed) this.recordDenial(domain, credential);
        await socket.send([version, requestId, allowed ? '200' : '400', allowed ? 'OK' : 'Peer key not admitted', '', '']);
      }
    } catch {
      // Socket closed under the loop (converge released it) — nothing to answer.
    }
  }

  private recordDenial(domain: string, credential?: Buffer) {
    const denial: ZapDenial = {
      domain,
      publicKey: credential?.length === 32 ? z85Encode(credential) : '(none)',
      at: Date.now(),
    };
    this.denials.push(denial);
    if (this.denials.length > MAX_DENIALS_KEPT) this.denials.shift();
    this.denialListeners.forEach(l => l(denial));
  }
}
